import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import {
	Button,
	Col,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import ataService from "services/AtaService";
import Swal from "sweetalert2";
import { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt-BR";
import userService from "services/UserService";
import Loading from "components/Common/Loading";
import { CheckSquare, FloppyDisk, Square } from "@phosphor-icons/react";
import { Checkbox } from "@mui/material";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
registerLocale("pt-BR", pt);

const DadosAta = (props) => {
	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const [data, setData] = useState(null);
	const [usuarios, setUsuarios] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [checked, setChecked] = useState(null);
	const [checked2, setChecked2] = useState(null);
	const [loading, setLoading] = useState(false);

	const id = props?.dataId;
	const podeVisualizar = props?.podeVisualizar;

	const navigate = useNavigate();

	const validation = useFormik({
		enableReinitialize: true,

		initialValues: {
			id: data?.id || null,
			name: data?.name || null,
			description: data?.description || null,
			collaborators: data?.collaborators || null,
			version: data?.version || 1,
			consultants_only: Number(data?.consultants_only) === 0 ? 0 : 1,
			print_description: data?.print_description || false,
		},

		validate: (values) => {
			const errors = {};

			if (!values.name) {
				errors.name = "Campo obrigatório";
			}
			if (!values.description) {
				errors.description = "Campo obrigatório";
			}

			return errors;
		},

		onSubmit: async (values, { setSubmitting }) => {
			const errors = await validation.validateForm(values);
			if (Object.keys(errors).length === 0) {
				try {
					setLoading(true);

					values.print_description = checked2;
					if (id) {
						await ataService.put(id, values);
					} else {
						await ataService.post(values);
					}

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Formulário enviado com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						setLoading(false);
						if (props.onNavigateToTab) {
							props.onNavigateToTab("encaminhamentos");
						} else {
							navigate("../atas");
						}
					});
				} catch (error) {
					SendaCustomSwal.fire({
						iconHtml: '<i class="error-icon"></i>',
						title: "Oops...",
						text: "Algo deu errado!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				} finally {
					setLoading(false);
				}
			}
			setSubmitting(false);
		},
	});

	const getItems = async () => {
		try {
			const response = await ataService.getById(id);
			response.collaborators = response.collaborators.map((item) => {
				return item.id;
			});
			setChecked(Number(response.consultants_only) === 1 ? true : false);
			setChecked2(response.print_description);
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	async function getUsuarios() {
		try {
			const response = await userService.get();
			const funcoes = response.map((item) => {
				return {
					value: item.id,
					label: item.lastname,
				};
			});
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		// Inicializa o estado do componente com os valores existentes do formulário em edição
		setSelectedOptions(
			usuarios?.filter((option) =>
				validation.values?.collaborators?.some(
					(item) => item === option.value,
				),
			) || [],
		);
	}, [usuarios, validation.values?.collaborators]);

	const handleChange = (selectedOption) => {
		setSelectedOptions(selectedOption);
	};

	const getAll = async () => {
		setIsLoading(true);
		if (id) {
			await getItems();
		}
		await getUsuarios();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, [id]);

	return (
		<Loading isLoading={isLoading}>
			<Form
				className="needs-validation"
				onSubmit={(e) => {
					e.preventDefault();
					validation.handleSubmit();
					return false;
				}}
				style={{ marginTop: 20 }}
			>
				<Row>
					<Col md="12">
						<FormGroup className="mb-3">
							<Label htmlFor="validationCustom01">
								Nome da Ata
							</Label>
							<Input
								name="name"
								placeholder="Nome da Ata"
								type="text"
								disabled={!podeVisualizar}
								className="form-control"
								id="validationCustom01"
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.name || ""}
								invalid={
									validation.touched.name &&
										validation.errors.name
										? true
										: false
								}
							/>
							{validation.touched.name &&
								validation.errors.name ? (
								<FormFeedback type="invalid">
									{validation.errors.name}
								</FormFeedback>
							) : null}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md="12">
						<FormGroup className="mb-3">
							<Label htmlFor="validationCustom01">
								Descrição
							</Label>
							<Input
								style={{ height: "350px" }}
								name="description"
								placeholder="Descrição"
								type="textarea"
								disabled={!podeVisualizar}
								className="form-control"
								id="validationCustom01"
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								required={true}
								value={validation.values.description || ""}
								invalid={
									validation.touched.description &&
										validation.errors.description
										? true
										: false
								}
							/>
							{validation.touched.description &&
								validation.errors.description ? (
								<FormFeedback type="invalid">
									{validation.errors.description}
								</FormFeedback>
							) : null}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					{podeVisualizar && (
						<Col md="12">
							<FormGroup className="mb-3">
								<Label htmlFor="validationCustom01">
									Quem pode ver
								</Label>
								<Select
									isMulti={true}
									styles={customStyles}
									placeholder="Selecione quem pode ver a ata"
									onChange={(selectedOption) => {
										handleChange(selectedOption);
										const selectedValue =
											selectedOption?.map(
												(selected) => selected.value,
											);
										validation.setFieldValue(
											"collaborators",
											selectedValue,
										);
									}}
									options={usuarios}
									value={selectedOptions}
								/>
							</FormGroup>
						</Col>
					)}
				</Row>
				<Row>
					{podeVisualizar && (
						<Col md="2">
							<FormGroup className="mb-3">
								<Checkbox
									id="consultants_only"
									name="consultants_only"
									defaultChecked={checked}
									onChange={(event) => {
										const value = event.target.checked
											? 1
											: 0;
										validation.setFieldValue(
											"consultants_only",
											value,
										);
									}}
									icon={<Square size={16} />}
									checkedIcon={<CheckSquare size={16} />}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										cursor: "pointer",
										color: "black",
									}}
								/>
								<Label
									className="form-check-label"
									htmlFor="consultants_only"
									style={{ marginLeft: 10 }}
								>
									Apenas consultores
								</Label>
							</FormGroup>
						</Col>
					)}
					<Col md="2">
						<FormGroup className="mb-3">
							<Checkbox
								id="print_description"
								name="print_description"
								defaultChecked={checked2}
								onChange={(e) => {
									setChecked2(e.target.checked);
									validation.setFieldValue(
										"print_description",
										e.target.checked,
									);
								}}
								icon={<Square size={16} />}
								checkedIcon={<CheckSquare size={16} />}
								style={{
									background: "none",
									border: "none",
									padding: 0,
									cursor: "pointer",
									color: "black",
								}}
							/>
							<Label
								className="form-check-label"
								htmlFor="print_description"
								style={{ marginLeft: 10 }}
							>
								Imprimir descrição
							</Label>
						</FormGroup>
					</Col>
				</Row>
				<div
					className="page-title-right"
					style={{ display: "flex", justifyContent: "end" }}
				>
					{/* {podeVisualizar && ( */}
					<Button
						type="submit"
						style={{
							background: "none",
							padding: 10,
							cursor: "pointer",
							color: "black",
							marginLeft: 10,
							width: 150,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 2,
						}}
					>
						{loading ? (
							<span
								className="spinner-border spinner-border-sm mr-1"
								style={{ marginRight: 10 }}
							></span>
						) : null}
						<FloppyDisk size={16} /> Salvar
					</Button>
					{/* )} */}
				</div>
			</Form>
		</Loading>
	);
};

DadosAta.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default DadosAta;
